<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div class="report-wrapper">
      <!-- Sticky Filter Header -->
      <div class="table-header-filters">
        <v-row dense class="align-center">
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportFromMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="filter-select"
                  :value="formatDateFrom"
                  label="Od datuma"
                  dense
                  outlined
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="reportFromMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportToMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="filter-select"
                  :value="formatDateTo"
                  label="Do datuma"
                  dense
                  outlined
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="reportToMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete
              class="filter-select"
              dense
              outlined
              hide-details
              label="Prodajno mjesto"
              v-model="location"
              :items="locations"
              item-text="name"
              item-value="id"
              return-object
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <categories-filter
              ref="categoriesFilter"
              :items="itemReport"
              @setIdsForFilter="onSetIdsForFilter"
            ></categories-filter>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2" class="d-flex align-center">
            <v-switch v-model="showUnpaid" label="Uključi neplaćene račune" hide-details class="mt-0 pt-0 custom-switch" color="#3989C9"></v-switch>
          </v-col>

          <!-- Action Cluster -->
          <v-col cols="auto" class="ml-auto d-flex align-center" style="gap: 8px">
            <v-btn :disabled="locationNotSelected" class="search-btn" @click="getData()" small>
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              Pretraži
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-menu offset-y left :close-on-content-click="true">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-btn icon class="export-btn" v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                      <v-icon color="#00b3be">mdi-export-variant</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense style="border-radius: 12px; overflow: hidden; padding: 4px; min-width: 140px;">
                    <v-list-item @click="changeWidth()">
                      <v-icon size="18" color="#E53935" class="mr-2">mdi-file-pdf-box</v-icon>
                      <v-list-item-title>PDF</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generateExcel()">
                      <v-icon size="18" color="#43A047" class="mr-2">mdi-file-excel</v-icon>
                      <v-list-item-title>Excel</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <span>Izvoz podataka</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="to" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Glass Table Wrapper -->
      <div class="glass-table-wrapper">
        <div id="table_info" class="table-content">
          <!-- Hidden company info table for PDF -->
          <table id="firma" style="display: none; font-family: 'Roboto-Regular'">
            <tbody>
              <tr>
                <td>{{ company ? $options.filters.capitalize(company.name) : "" }}</td>
              </tr>
              <tr>
                <td>{{ company ? company.address + " " + company.city : "" }}</td>
              </tr>
              <tr>
                <td>OIB: {{ company ? company.oib : "" }}</td>
              </tr>
              <tr>
                <td>Prodajno mjesto: {{ location ? location.name : "" }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Report Title -->
          <div style="font-family: Raleway, sans-serif;">
            <table justify="center" style="width: 100% !important" cellspacing="0" id="title">
              <thead>
                <tr class="header">
                  <th colspan="7">
                    <p class="gradient-text" style="font-size: 25px; text-align: center;">
                      Prometa artikala
                    </p>
                  </th>
                </tr>
                <tr class="pa-0 ma-0">
                  <th colspan="7">
                    <p style="font-weight: bold; font-size: 12px; text-align: center;">
                      za razdoblje od {{ formatDateFrom }} do {{ formatDateTo }}
                    </p>
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          <!-- Item Report Table -->
          <div justify="center">
            <table
              justify="center"
              style="width: 100% !important"
              cellspacing="0"
              id="tableTraffic"
            >
              <thead>
                <tr :class="['header', 'naslov']" style="display: none">
                  <th colspan="7">
                    <span style="font-weight: bold">Promet po artiklima</span>
                  </th>
                </tr>
                <tr :class="['header', 'naslov']">
                  <th style="text-align: left">Artikl</th>
                  <th style="text-align: right">Količina</th>
                  <th style="text-align: right">Cijena</th>
                  <th style="text-align: right">Popust</th>
                  <th style="text-align: right">Netto prodaja</th>
                  <th style="text-align: right">PDV</th>
                  <th style="text-align: right">Netto iznos</th>
                </tr>
              </thead>
              <tbody style="width: 100% !important">
                <tr v-for="(item, itIdx) in itemReport" :key="item.id + itIdx">
                  <td style="text-align: left">{{ item.name }}</td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (item.quantity / 1000).toFixed(3).replace(".", ",")
                        : (item.quantity / 1000).toFixed(3)
                    }}
                  </td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (item.singleItemPrice / 100).toFixed(2).replace(".", ",")
                        : (item.singleItemPrice / 100).toFixed(2)
                    }}
                  </td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (item.discount / 100).toFixed(2).replace(".", ",")
                        : (item.discount / 100).toFixed(2)
                    }}
                  </td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (item.nettoSales / 100).toFixed(2).replace(".", ",")
                        : (item.nettoSales / 100).toFixed(2)
                    }}
                  </td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (item.pdvAmount / 100).toFixed(2).replace(".", ",")
                        : (item.pdvAmount / 100).toFixed(2)
                    }}
                  </td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (item.nettoAmount / 100).toFixed(2).replace(".", ",")
                        : (item.nettoAmount / 100).toFixed(2)
                    }}
                  </td>
                </tr>

                <!-- Ukupno redak -->
                <tr v-if="itemReport && itemReport.length > 0" class="row-total">
                  <th style="text-align: left">Ukupno</th>
                  <th style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (totalReport.quantity / 1000).toFixed(3).replace(".", ",")
                        : (totalReport.quantity / 1000).toFixed(3)
                    }}
                  </th>
                  <th style="text-align: right">—</th>
                  <th style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (totalReport.discount / 100).toFixed(2).replace(".", ",")
                        : (totalReport.discount / 100).toFixed(2)
                    }}
                  </th>
                  <th style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (totalReport.nettoSales / 100).toFixed(2).replace(".", ",")
                        : (totalReport.nettoSales / 100).toFixed(2)
                    }}
                  </th>
                  <th style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (totalReport.pdvAmount / 100).toFixed(2).replace(".", ",")
                        : (totalReport.pdvAmount / 100).toFixed(2)
                    }}
                  </th>
                  <th style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? (totalReport.nettoAmount / 100).toFixed(2).replace(".", ",")
                        : (totalReport.nettoAmount / 100).toFixed(2)
                    }}
                  </th>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { DAY, clone } from '@/plugins/utils'
import { df, auth } from '@/plugins/firebase'
import state from '@/state'
import 'jspdf-autotable'
import { saveExcel } from '@/modules/all-reports/xlsx/config.js'
import EventBus from '@/plugins/event-bus'
import { initPdf, setTable, setPageNumber } from '@/modules/all-reports/pdf/config.js'
import CategoriesFilter from '@/components/CategoriesFilter.vue'
import avansDiscount from '@/mixins/avans'

export default {
  name: 'Reports',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  components: { CategoriesFilter },
  data: function () {
    return {
      dateFrom: new Date(new Date().getTime() - DAY).toISOString().substr(0, 10),
      dateTo: new Date().toISOString().substr(0, 10),
      allReceipts: [],
      reportFromMenu: undefined,
      reportToMenu: undefined,
      company: undefined,
      tableWidth: '1200px',
      a4PortraitWidth: '575px',
      a4LandscapeWidth: '610px',
      targetModifier: '',
      viewType: 'pdf',
      itemReport: [],
      totalReport: {},
      currency: 'EUR',
      locations: [],
      location: null,
      showUnpaid: true,
      margin: '0px',
      filters: []
    }
  },
  computed: {
    to () {
      return { name: `${this.targetModifier}.all-reports.reportMenu` }
    },
    formatDateTo () {
      return new Intl.DateTimeFormat('hr-HR', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).format(new Date(this.dateTo))
    },
    formatDateFrom () {
      return new Intl.DateTimeFormat('hr-HR', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).format(new Date(this.dateFrom))
    },
    locationNotSelected () {
      return !this.location || !this.location.id
    }
  },
  async created () {
    this.margin = state.getMargin()
    await this.getPointsOfSale()
  },
  mounted () {
    var sideNav = this.$route.meta.sideNav
    switch (sideNav) {
      case 'Companies':
        break
      case 'Company':
        this.targetModifier = 'company'
        break
      case 'PointOfSale':
        this.targetModifier = 'pointOfSale'
        break
      case 'CashRegister':
        this.targetModifier = 'pointOfSale'
        break
    }

    EventBus.$on('set-mini', () => {
      this.margin = state.getMargin()
    })

    this.company = clone(state.getCurrentCompany())
    this.location = clone(state.getPointOfSale())

    if (this.company.type === 'pausalni_obrt') {
      this.showUnpaid = false
    } else {
      this.showUnpaid = true
    }
  },
  methods: {
    onSetIdsForFilter (ids) {
      this.filters = []
      this.filters.push(ids)
    },

    async getData () {
      this.showLoader()

      const dateFrom = new Date(this.dateFrom).getTime() / 1000
      const dateTo = new Date(this.dateTo).getTime() / 1000

      this.allReceipts = []

      let query = df
        .collection(`receipts/${state.getCurrentCompany().id}/receipts`)
        .where('issue_date_time', '>=', dateFrom)
        .where('issue_date_time', '<=', dateTo + 86399)
      if (this.location && this.location.id !== 'all') {
        query = query.where('location_details.id', '==', this.location.id)
      }

      await query.get().then((documentSnapshots) => {
        if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
          documentSnapshots.docs.forEach((item) => {
            var rcpt = item.data()
            if (this.showUnpaid === false) {
              if (rcpt.payment && (rcpt.payment.current_status === 'PAID' || rcpt.payment.current_status === 'REVERSED')) {
                this.allReceipts.push(item.data())
              }
            } else {
              this.allReceipts.push(item.data())
            }
          })
        }
      })

      this.allReceipts.sort((a, b) => a.issue_date_time - b.issue_date_time)

      this.calculateData()
    },
    async calculateData () {
      this.allReceipts.forEach((receipt) => {
        // Micanje poreza ako je dodan popust na iznos
        if (receipt && receipt.discount && receipt.discount.amount > 0 && receipt.discount.name !== 'Reprezentacija') {
          let totalRemove = receipt.discount.amount / 100

          if (receipt.discount.name === 'AVANS-STORNO-RECEIPT') {
            if (receipt.type.flag !== 'STORNO') {
              receipt.pdv = avansDiscount(receipt.pdv, receipt.tags)
            }
          } else {
            receipt.pdv.forEach((p, pkey) => {
              if (p.base > 0 && p.total > 0) {
                let totalPrice = p.base + p.total

                if (totalPrice >= 1 + p.rate / 10000) {
                  let removedAllPossible = false
                  let remove = totalRemove

                  // kreni unazad, uzmi ukupni iznos za skidanje i probaj skinuti
                  for (var j = totalRemove; j > 0 && removedAllPossible === false; j--) {
                    // ako oduzmes iznos i on je veci od 100 na kraju i ustvari si skinuo sve, tu zavrsi
                    if (totalPrice - j >= 1 + p.rate / 10000) {
                      removedAllPossible = true
                      remove = j
                    }
                  }

                  if (removedAllPossible === true) {
                    totalPrice -= remove
                    totalRemove -= remove
                  }
                }

                receipt.pdv[pkey].base = parseInt(parseFloat(totalPrice) / (1 + parseFloat(receipt.pdv[pkey].rate) / 10000))
                receipt.pdv[pkey].total = totalPrice - receipt.pdv[pkey].base
              }
            })
          }
        }
      })

      var itemReport = []
      var totalReport = {
        quantity: 0,
        discount: 0,
        nettoSales: 0,
        pdvAmount: 0,
        nettoAmount: 0
      }

      var hasWarehouse = state.getCurrentCompany().warehouse === 'module'

      this.itemReport = []
      this.allReceipts.forEach((receipt) => {
        if (receipt.items && receipt.items.length > 0) {
          receipt.items.forEach((item) => {
            if (!hasWarehouse || (hasWarehouse && item.id.includes('.'))) {
              const pdvRate = (item.pdv?.rate || 0) / 10000
              const qty = item.amt / 1000
              // single_item_price je u centima (po jedinici)
              const brutePerLine = Math.round((item.single_item_price || 0) * qty)
              const nettoSales = item.price || 0 // u centima, nakon popusta, s PDV-om
              const discount = Math.max(0, brutePerLine - nettoSales)
              const nettoAmount = pdvRate > 0 ? Math.round(nettoSales / (1 + pdvRate)) : nettoSales
              const pdvAmount = nettoSales - nettoAmount

              var idx = itemReport.findIndex((p) => p?.id === item.id)
              if (idx > -1) {
                itemReport[idx].quantity += item.amt
                itemReport[idx].discount += discount
                itemReport[idx].nettoSales += nettoSales
                itemReport[idx].pdvAmount += pdvAmount
                itemReport[idx].nettoAmount += nettoAmount
              } else {
                itemReport.push({
                  id: item.id,
                  name: item.name,
                  quantity: item.amt,
                  singleItemPrice: item.single_item_price || 0, // u centima
                  discount: discount, // u centima
                  nettoSales: nettoSales, // u centima, s PDV-om, nakon popusta
                  pdvAmount: pdvAmount, // u centima
                  nettoAmount: nettoAmount // u centima, bez PDV-a
                })
              }
            }
          })
        }
      })

      if (this.filters && this.filters.flat().length > 0) {
        const newItemReport = await this.$refs.categoriesFilter.getCategoriesByItemId(itemReport, this.location.id)
        itemReport = newItemReport
      }

      this.itemReport = itemReport
      this.itemReport.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))

      itemReport.forEach((item) => {
        totalReport.quantity += item.quantity
        totalReport.discount += item.discount
        totalReport.nettoSales += item.nettoSales
        totalReport.pdvAmount += item.pdvAmount
        totalReport.nettoAmount += item.nettoAmount
      })

      this.totalReport = totalReport
      this.hideLoader()
    },
    changeWidth () {
      this.showLoader('', { opacity: 1 })
      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = this.a4PortraitWidth
      tableInfo.style.fontSize = '80%'

      this.generatePdf()
    },
    async generatePdf () {
      const that = this
      const tableInfo = document.getElementById('table_info')
      try {
        var doc = await initPdf()

        doc = await setTable(doc, '#firma', 10)
        var finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#title', finalY)
        finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#tableTraffic', finalY)

        doc = await setPageNumber(doc)

        doc.save('Rekapitulacija prometa artikala.pdf')
      } catch (error) {
        console.error('PDF generation error:', error)
      } finally {
        tableInfo.style.width = that.tableWidth
        tableInfo.style.fontSize = '100%'
        this.viewType = 'pdf'
        that.hideLoader()
      }
    },
    generateExcel () {
      this.showLoader('', { opacity: 1 })
      this.viewType = 'excel'
      setTimeout(() => {
        var tables = ['firma', 'title', 'tableTraffic']
        var fileName = 'Rekapitulacija prometa artikala.xlsx'

        saveExcel(tables, fileName)

        this.viewType = 'pdf'
        this.hideLoader()
      }, 1000)
    },
    async getPointsOfSale () {
      const docRef = df.doc(`user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`)
      docRef.onSnapshot((doc) => {
        var locations = Object.values(doc.data().locations)
        locations.forEach((location, index) => {
          this.locations.push(location)
        })
        // Sort alphabetically
        this.locations.sort((a, b) => a.name.localeCompare(b.name))
      })
    }
  }
}
</script>

<style scoped>
/* Report Wrapper */
.report-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

/* Sticky Filter Header */
.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

.table-header-filters >>> .v-row {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.table-header-filters >>> .v-row:first-child {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

/* Filter Select Inputs */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

/* Categories Filter Styling */
categories-filter >>> .v-text-field--outlined fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

categories-filter >>> .v-text-field--outlined:hover fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

categories-filter >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

categories-filter >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

categories-filter >>> .v-select__selection,
categories-filter >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

categories-filter >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* Switch Wrapper */
.switch-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  padding-top: 4px;
}

.custom-switch >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.custom-switch >>> .v-label {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 500;
}

.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.search-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.search-btn:hover:not(:disabled) {
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}

.search-btn:disabled {
  opacity: 0.45 !important;
}

.export-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(0, 179, 190, 0.06) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  transition: all 0.2s ease !important;
}

.export-btn:hover {
  background: rgba(0, 179, 190, 0.12) !important;
  box-shadow: 0 0 0 3px rgba(0, 179, 190, 0.12) !important;
}

/* Glass Table Wrapper */
.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 30px;
  overflow-x: auto;
}

/* Table Content */
.table-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  font-size: 16px;
  font-family: 'Roboto-Regular', sans-serif;
}

/* Gradient Text */
.gradient-text {
  font-weight: 700 !important;
  color: #2d3748 !important;
  -webkit-text-fill-color: #2d3748 !important;
  letter-spacing: 0.3px !important;
  display: inline-block !important;
  margin: 0;
  padding: 0;
}

/* Table Styling */
#tableTraffic {
  border-collapse: collapse;
}

.glass-table-wrapper table {
  border-collapse: collapse;
  width: 100%;
  border: none;
}

.glass-table-wrapper table th,
.glass-table-wrapper table td {
  padding: 14px 20px;
  border: none;
  border-bottom: 1px solid #EDF2F7;
  font-variant-numeric: tabular-nums;
}

.glass-table-wrapper table tbody tr:hover:not(.naslov):not(.row-total) {
  background: #F7FAFC;
}

/* Header Row */
.naslov {
  background: #F8FAFC !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  color: #2D3748 !important;
}
.naslov th, .naslov td {
  color: #2D3748 !important;
  border-bottom: 2px solid #EDF2F7 !important;
}

.row-total {
  background: rgba(0, 188, 212, 0.03) !important;
  border-top: 2px solid rgba(0, 188, 212, 0.2) !important;
}
.row-total th, .row-total td {
  color: #00b3be !important;
  font-weight: 700 !important;
  border-bottom: 1px solid rgba(0, 188, 212, 0.15) !important;
}

.report-content-wrapper {
  margin-top: 40px;
}

/* Responsive Design */
@media (max-width: 960px) {
  .glass-table-wrapper {
    padding: 16px;
  }

  .table-content {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .table-header-filters {
    padding: 12px;
  }

  .glass-table-wrapper {
    padding: 12px;
  }

  .table-content {
    font-size: 13px;
  }
}
</style>
